<script>
    /** @type {import('./$types').LayoutData} */
    import '~/app.css';
    import { fly } from 'svelte/transition';
    import { cubicOut } from 'svelte/easing';

    import { onMount, setContext } from 'svelte';
    import { beforeNavigate } from '$app/navigation';
    import { initializeCart } from '$lib/utils/shopify/cart/initialize';
    import { pwaPromptListener, registerServiceWorker } from '$lib/utils/pwa';

    import Header from './Header.svelte';
    import Footer from './Footer.svelte';
    import Drawer from '$lib/components/Drawer.svelte';
    import CartLayout from '$lib/components/CartLayout.svelte';
    import SiteNotification from '$lib/components/SiteNotification.svelte';
    import NavLayout from '$lib/components/NavLayout.svelte';
    import PageLoading from '$lib/components/PageLoading.svelte';
    import AgePrompt from '$lib/components/AgePrompt.svelte';
    import InsightWise from '$lib/utils/InsightWise/InsightWise.svelte';
    import Context from '$lib/utils/context';
    //    import PwaDebug from '$lib/utils/pwa/PWADebug.svelte';

    export let data;

    let drawerMenuOpen = false;
    let drawerCartOpen = false;
    let headerNavData = data?.site?.headerMenu;

    // Effects
    $: inCheckout = data.pathname.includes('/checkout');
    $: redispatch({ inCheckout });

    // Initalizers for when any route first hydrates
    let alreadyMounted;

    // Context to be used down in deeply nested parts without prop drilling; Cart, SectionPosition, etc.
    const context = Context(); // Context is a function so it gets initalized each request.
    setContext('ctx', context);
    setContext('pathname', () => data.pathname);

    onMount(async () => {
        // Store Site config
        window.config = data.config;
        localStorage.setItem('config', JSON.stringify(data.config));

        initializeCart({ persistent: data?.user?.persistent }); // Cart system
        registerServiceWorker(context); // Async After-load register
        pwaPromptListener(); // PWA Install Listeners

        alreadyMounted = true;

        // ?cartOpen=true (redirect from /cart)
        if (data.cartOpen)
            setTimeout(() => {
                drawerCartOpen = true;
            }, 1);
    });

    /**
     * Redispatch our one-time mount cart if we have destroyed the layout (checkout)
     * @param destroyerChanges
     */
    const redispatch = ({ inCheckout }) => {
        if (alreadyMounted && !inCheckout) {
            initializeCart({ persistent: data?.user?.persistent });
        }
    };

    beforeNavigate(() => {
        context.sectionPosition.resetPosition(); // Reset section positions before rendering the next page.
    });
</script>

<svelte:head>
    {#if data?.config?.indexAndFollow !== true}
        <meta name="robots" content="noindex,nofollow" />
    {/if}
    {#if data.promptAge}
        <style>
            body {
                overflow: hidden !important;
            }
        </style>
    {/if}
</svelte:head>

<PageLoading />
<!--<PwaDebug />-->
{#if inCheckout}
    <main class="min-h-screen" in:fly={{ easing: cubicOut, y: 60, duration: 300, delay: 0 }}>
        <slot />
    </main>
{:else}
    {#if data.promptAge}
        <AgePrompt />
    {/if}
    <Header bind:drawerMenuOpen bind:drawerCartOpen featuredMenu={data?.site?.featuredMenu} />

    {#key data.pathname}
        <Drawer position="left" name="nav" bind:drawerOpen={drawerMenuOpen}>
            <NavLayout bind:drawerMenuOpen data={headerNavData?.menuItems} />
        </Drawer>

        <main class="min-h-screen lg:pt-20" in:fly={{ easing: cubicOut, y: 60, duration: 300, delay: 0 }}>
            <slot />
        </main>
    {/key}

    <Drawer position="right" name="cart" bind:drawerOpen={drawerCartOpen} let:setDrawerCallback>
        <CartLayout {setDrawerCallback} />
    </Drawer>

    <SiteNotification />

    <Footer />
{/if}
<InsightWise user={data.user} />
